import type {
  MemberAddressResponse,
  MemberBillingAddressRequest,
  MemberShippingAddressRequest,
} from "@features/authentication/types";

import { memberService } from "@features/authentication/api/member";
import { handleApiError } from "@shared/utils/error";
import { defineStore } from "pinia";

type MemberState = {
  addresses: MemberAddressResponse | null;
  error: null | string;
  loading: boolean;
};

export const useMemberStore = defineStore("member", {
  actions: {
    clearError() {
      this.error = null;
    },

    clearMember() {
      this.addresses = null;
    },

    async getAddress(memberId: string) {
      this.loading = true;
      this.error = null;

      try {
        const response = await memberService.getAddress(memberId);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        this.addresses = response;

        return response;
      } catch (err) {
        this.error = handleApiError(err, "Failed to fetch addresses");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async saveBillingAddress(data: MemberBillingAddressRequest) {
      this.loading = true;
      this.error = null;

      try {
        const oparetor = this.billingAddress?.billing_addr_id
          ? memberService.updateBillingAddress.bind(memberService)
          : memberService.createBillingAddress.bind(memberService);

        const response = await oparetor(data);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        // Refresh addresses after creation
        if (data.member_id) {
          await this.getAddress(data.member_id);
        }

        return response;
      } catch (err) {
        this.error = handleApiError(err, "Failed to update billing address");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async saveShippingAddress(data: MemberShippingAddressRequest) {
      this.loading = true;
      this.error = null;

      try {
        const oparetor = this.shippingAddress?.shipping_addr_id
          ? memberService.updateShippingAddress.bind(memberService)
          : memberService.createShippingAddress.bind(memberService);

        const response = await oparetor(data);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        // Refresh addresses after creation
        if (data.member_id) {
          await this.getAddress(data.member_id);
        }

        return response;
      } catch (err) {
        this.error = handleApiError(err, "Failed to update shipping address");
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },

  getters: {
    billingAddress: (state) => state.addresses?.billing_address,
    getAddresses: (state) => state.addresses,
    hasAddresses: (state) => !!state.addresses,
    shippingAddress: (state) => state.addresses?.shipping_address,
  },

  state: (): MemberState => ({
    addresses: null,
    error: null,
    loading: false,
  }),
});
